import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Grid } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import moment from 'moment';
import { DateRange } from '@material-ui/pickers';
import { useApiError, useAppData, useLoading } from '../../../providers';
import { ReportTitle, Header, PageTitle, QuickReportWithPercentage } from '../../../components';
import { DailyReportRequestData, MonthlyReportRequestData, DailyEmailReportRequest, MonthlyEmailReportRequest, reportEnColumns, TradingSummaryReport, ReportItem } from './TradingSummarytModel';
import { getTradingDailyReport, getTradingMonthlyReport, sendEmailTradingDailySummary, sendEmailTradingMonthlySummary } from './TradingSummaryService';
import { ReportsToolbar, TradingReports } from './components';
import { useStyles } from './styles';
import { getSelectedColumnsInEn, getTranslatedColumnsInCorrectOrder } from '../../../utils';
import { trackAmplitudeData } from '../../../services/amplitudeService';
import { useCommonStyles } from '../../../styles';
import { ColumnConfig, formatNumber, formatDate, formatAbsolute } from '../DataTable';
import { formatInteger } from '../DataTable/formatters';

export const TradingSummary: FC = () => {
  const classes = useStyles();
  const commonClasses = useCommonStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { userData, translations } = useAppData();
  const { addError } = useApiError();
  const { setIsLoading } = useLoading();
  const [selectedDateRange, setSelectedDateRange] = React.useState<DateRange<Date>>([moment().subtract(1, 'months').toDate(), moment().subtract(1, 'days').toDate()]);
  const [reportsData, setReportsData] = useState<TradingSummaryReport>();
  const [showReport, setShowReport] = useState<boolean>(false);
  const [isDaily, setIsDaily] = useState<boolean>(true);
  const [translation, setTranslation] = useState<any>([]);
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [userEmail, setUserEmail] = useState<string>();
  const [accountGuidAuth, setAccountGuidAuth] = useState<string | undefined>();

  useEffect(() => {
    setTranslation(JSON.parse(translations));
  }, [translations]);

  useEffect(() => {
    const user = JSON.parse(userData);
    setUserEmail(user.userEmail);
    setAccountGuidAuth(user?.accounts[0].accountGuid || '');
  }, [userData]);

  const columns = useMemo(
    (): ColumnConfig<ReportItem>[] => [
      { key: 'date', label: translation.text_6571 || '', type: 'date', format: (v) => formatDate(v) },
      { key: 'trades', label: translation.text_6572 || '', type: 'number', format: (v) => formatInteger(v), summable: true },
      { key: 'tradingAccountsTraded', label: translation.text_6573 || '', type: 'number', format: (v) => formatInteger(v), summable: true },
      { key: 'depositsUSD', label: translation.text_6574 || '', type: 'number', format: (v) => formatNumber(v), summable: true },
      { key: 'withdrawalsUSD', label: translation.text_6575 || '', type: 'number', format: (v) => formatAbsolute(v), summable: true },
      { key: 'valueUSD', label: translation.text_6576 || '', type: 'number', format: (v) => formatNumber(v), summable: true },
      { key: 'lots', label: translation.text_6577 || '', type: 'number', format: (v) => formatNumber(v), summable: true },
      { key: 'volumeLots', label: translation.text_6578 || '', type: 'number', format: (v) => formatNumber(v), summable: true },
      { key: '_empty', label: translation.text_6579 || '', sortable: false, format: () => '-' },
      { key: 'tradingAccountsFunded', label: translation.text_6580 || '', type: 'number', format: (v) => formatInteger(v), summable: true },
      { key: 'netDepositsUSD', label: translation.text_6581 || '', type: 'number', format: (v) => formatNumber(v), summable: true },
    ],
    [translation],
  );

  // Derived string[] needed only for email utilities (getSelectedColumnsInEn / getTranslatedColumnsInCorrectOrder)
  const columnNames = useMemo(() => columns.map((col) => col.label), [columns]);

  useEffect(() => {
    if (columnNames.length === 11) {
      setSelectedColumns(columnNames.slice(0, 8));
    }
  }, [columnNames]);

  const handleNewReportRequest = useCallback(
    (daily: boolean) => {
      setIsLoading(true);

      trackAmplitudeData('partnerPortal_action_report', {
        type: 'Requested',
        report_name: 'Trading Summary',
      });

      if (selectedDateRange[0] && selectedDateRange[1]) {
        if (daily) {
          const data: DailyReportRequestData = {
            email: '',
            partnershipCode: '',
            brokerDomainId: '',
            dayFrom: selectedDateRange[0].getDate(),
            dayTo: selectedDateRange[1].getDate(),
            monthFrom: selectedDateRange[0].getMonth() + 1,
            yearFrom: selectedDateRange[0].getFullYear(),
            monthTo: selectedDateRange[1].getMonth() + 1,
            yearTo: selectedDateRange[1].getFullYear(),
            filterPlatformType: 1,
            accountTypeFilter: 1,
          };

          getTradingDailyReport(accountGuidAuth, data)
            .then((response) => {
              setReportsData(response.data);
              setShowReport(true);
            })
            .catch((error) => {
              addError(JSON.stringify(error.response));
            })
            .finally(() => {
              setIsLoading(false);
            });
        } else {
          const data: MonthlyReportRequestData = {
            email: '',
            partnershipCode: '',
            brokerDomainId: '',
            monthFrom: selectedDateRange[0].getMonth() + 1,
            yearFrom: selectedDateRange[0].getFullYear(),
            monthTo: selectedDateRange[1].getMonth() + 1,
            yearTo: selectedDateRange[1].getFullYear(),
            filterPlatformType: 1,
            accountTypeFilter: 1,
          };

          getTradingMonthlyReport(accountGuidAuth, data)
            .then((response) => {
              setReportsData(response.data);
              setShowReport(true);
            })
            .catch((error) => {
              addError(JSON.stringify(error.response));
            })
            .finally(() => {
              setIsLoading(false);
            });
        }
      }
    },
    [accountGuidAuth, selectedDateRange, addError, setIsLoading],
  );

  const buildDailyEmailReportRequest = (): DailyEmailReportRequest => ({
    registrationsDailyFilters: {
      email: null,
      brokerFID: 0,
      partnershipCode: '',
      yearFrom: selectedDateRange[0]?.getFullYear(),
      yearTo: selectedDateRange[1]?.getFullYear(),
      monthFrom: (selectedDateRange[0]?.getMonth() || 0) + 1,
      monthTo: (selectedDateRange[1]?.getMonth() || 0) + 1,
      filterPlatformType: 1,
      filterAccountType: 1,
      dayFrom: selectedDateRange[0]?.getDate(),
      dayTo: selectedDateRange[1]?.getDate(),
    },
    reportEmailRequest: {
      email: null,
      includeTotal: true,
      tableColumns: getSelectedColumnsInEn(reportEnColumns, selectedColumns, columnNames),
      translatedTableColumns: getTranslatedColumnsInCorrectOrder(selectedColumns, columnNames),
      startDate: moment(selectedDateRange[0]).format('YYYY-MM-DD'),
      endDate: moment(selectedDateRange[1]).format('YYYY-MM-DD'),
    },
    accountGuidAuth,
  });

  const buildMonthlyEmailReportRequest = (): MonthlyEmailReportRequest => ({
    registrationsMonthlyFilters: {
      email: null,
      brokerFID: 0,
      partnershipCode: '',
      yearFrom: selectedDateRange[0]?.getFullYear(),
      yearTo: selectedDateRange[1]?.getFullYear(),
      monthFrom: (selectedDateRange[0]?.getMonth() || 0) + 1,
      monthTo: (selectedDateRange[1]?.getMonth() || 0) + 1,
      filterPlatformType: 1,
      filterAccountType: 1,
    },
    reportEmailRequest: {
      email: null,
      includeTotal: true,
      tableColumns: getSelectedColumnsInEn(reportEnColumns, selectedColumns, columnNames),
      translatedTableColumns: getTranslatedColumnsInCorrectOrder(selectedColumns, columnNames),
      startDate: moment(selectedDateRange[0]).format('YYYY-MM-DD'),
      endDate: moment(selectedDateRange[1]).format('YYYY-MM-DD'),
    },
    accountGuidAuth,
  });

  const sendEmail = async () => {
    trackAmplitudeData('partnerPortal_action_report', {
      type: 'SendByEmail',
      report_name: 'Trading Summary',
    });

    if (isDaily) {
      const dailyEmailReportRequest = buildDailyEmailReportRequest();
      try {
        await sendEmailTradingDailySummary(dailyEmailReportRequest);
        enqueueSnackbar(`${translation.text_1640} ${userEmail}.`, { variant: 'success' });
      } catch (error) {
        addError(JSON.stringify(error.response));
      } finally {
        setIsLoading(false);
      }
    } else {
      try {
        const monthlyEmailReportRequest = buildMonthlyEmailReportRequest();
        await sendEmailTradingMonthlySummary(monthlyEmailReportRequest);
        enqueueSnackbar(`${translation.text_1640} ${userEmail}.`, { variant: 'success' });
      } catch (error) {
        addError(JSON.stringify(error.response));
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <>
      <Header title={translation.text_6663} />
      <Grid container direction="column" className={classes.root}>
        <PageTitle title={translation.text_6663} />
        <Grid container direction="column" className={commonClasses.mainContainer}>
          <ReportTitle
            title={translation.text_6411}
            subTitle={translation.trading_summary_tooltip}
            exportFileName={translation.text_6411}
            columns={columns}
            allData={reportsData?.items || []}
            selectedColumns={selectedColumns}
            printColumnNumber={selectedColumns.length}
            content={<TradingReports key={Math.random()} translation={translation} reportData={reportsData} selectedColumns={selectedColumns} columns={columns} onSelectColumns={setSelectedColumns} />}
            enableButtons={showReport}
            translation={translation}
            sendEmail={sendEmail}
            reportNameInEn="Trading Summary"
          />
          <Grid container direction="column">
            <ReportsToolbar
              translation={translation}
              dateRange={selectedDateRange}
              handleDateRangeChange={(value) => {
                setSelectedDateRange(value);
                setShowReport(false);
                setReportsData(undefined);
              }}
              showButton
              handlePeriodChange={(daily) => {
                setIsDaily(daily);
                setShowReport(false);
                setReportsData(undefined);
              }}
              handleButtonClick={() => handleNewReportRequest(isDaily)}
              applyFilters={(daily) => {
                setIsDaily(daily);
                setReportsData(undefined);
                handleNewReportRequest(daily);
              }}
            />
            <Grid item xs={12}>
              <Grid container direction="row" spacing={2} className={classes.panels}>
                <Grid item className={classes.quickReports}>
                  <QuickReportWithPercentage title={translation.text_6572} value={reportsData?.total.tradesTotal || 0} percentage={reportsData?.total.tradesTotalPercentage || 0} />
                </Grid>
                <Grid item className={classes.quickReports}>
                  <QuickReportWithPercentage title={translation.text_6573} value={reportsData?.total.tradingAccountsTradedTotal || 0} percentage={reportsData?.total.tradingAccountsTradedTotalPercentage || 0} />
                </Grid>
                <Grid item className={classes.quickReports}>
                  <QuickReportWithPercentage title={translation.text_6574} value={reportsData?.total.depositsUSDTotal || 0} percentage={reportsData?.total.depositUsdTotalPercentage || 0} />
                </Grid>
                <Grid item className={classes.quickReports}>
                  <QuickReportWithPercentage title={translation.text_6575} value={reportsData?.total.withdrawalsUsdTotal || 0} percentage={reportsData?.total.withdrawalsUsdTotalPercentage || 0} />
                </Grid>
                <Grid item className={classes.quickReports}>
                  <QuickReportWithPercentage title={translation.text_6576} value={reportsData?.total.valueUsdTotal || 0} percentage={reportsData?.total.valueUsdTotalPercentage || 0} />
                </Grid>
                <Grid item className={classes.quickReports}>
                  <QuickReportWithPercentage title={translation.text_6577} value={reportsData?.total.lotsTotal || 0} percentage={reportsData?.total.lostTotalPercentage || reportsData?.total.volumeLotsTotalPercentage || 0} />
                </Grid>
                <Grid item className={classes.quickReports}>
                  <QuickReportWithPercentage title={translation.text_6578} value={reportsData?.total.volumeLotsTotal || 0} percentage={reportsData?.total.volumeLotsTotalPercentage || reportsData?.total.valueUsdTotalPercentage || 0} />
                </Grid>
                <Grid item className={classes.quickReports}>
                  <QuickReportWithPercentage title={translation.text_6580} value={reportsData?.total.tradingAccountsFundedTotal || 0} percentage={reportsData?.total.tradingAccountsFundedTotalPercentage || 0} />
                </Grid>
                <Grid item className={classes.quickReports}>
                  <QuickReportWithPercentage title={translation.text_6581} value={reportsData?.total.netDepositsUsdTotal || 0} percentage={reportsData?.total.netDepositsUsdTotalPercentage || 0} />
                </Grid>
              </Grid>
              <TradingReports key={Math.random()} translation={translation} reportData={reportsData} selectedColumns={selectedColumns} columns={columns} onSelectColumns={setSelectedColumns} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};
